import { SearchOutlined } from '@ant-design/icons'
import InputDebounce from '@/components/forms/InputDebounce'
import { StatusTabs } from '@/components/shared/StatusTabs'
import { FilterPopover } from '@/components/shared/FilterPopover'
import { DateRangeFilter } from '@/components/shared/DateRangeFilter'
import { ClearFiltersButton } from '@/components/shared/ClearFiltersButton'
import { Category } from '@/types/events'
import { EventOwner } from '@/pages/admin/event_owners/index.page'
import dayjs from 'dayjs'

interface EventsTableToolbarProps {
  searchText: string
  onSearchChange: (value: string) => void
  activeTab: string
  onTabChange: (tab: string) => void
  owners: EventOwner[]
  categories: Category[]
  selectedOwners: string[]
  onOwnersChange: (ids: string[]) => void
  selectedCategories: string[]
  onCategoriesChange: (ids: string[]) => void
  selectedDateRange: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null
  onDateRangeChange: (dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => void
  ownerFilterVisible: boolean
  onOwnerFilterVisibleChange: (visible: boolean) => void
  categoryFilterVisible: boolean
  onCategoryFilterVisibleChange: (visible: boolean) => void
  dateFilterVisible: boolean
  onDateFilterVisibleChange: (visible: boolean) => void
  onClearFilters: () => void
}

const statusTabs = [
  { key: 'all', label: 'All' },
  { key: 'live', label: 'Live' },
  { key: 'draft', label: 'Draft' },
  { key: 'archived', label: 'Archived' },
  { key: 'purged', label: 'Purged' },
  { key: 'deleted', label: 'Deleted' },
]

export function EventsTableToolbar({
  searchText,
  onSearchChange,
  activeTab,
  onTabChange,
  owners,
  categories,
  selectedOwners,
  onOwnersChange,
  selectedCategories,
  onCategoriesChange,
  selectedDateRange,
  onDateRangeChange,
  ownerFilterVisible,
  onOwnerFilterVisibleChange,
  categoryFilterVisible,
  onCategoryFilterVisibleChange,
  dateFilterVisible,
  onDateFilterVisibleChange,
  onClearFilters,
}: EventsTableToolbarProps) {
  const hasActiveFilters =
    selectedOwners.length > 0 ||
    selectedCategories.length > 0 ||
    selectedDateRange !== null ||
    activeTab !== 'all' ||
    searchText !== ''

  return (
    <div className="px-6 py-4">
      <div className="flex justify-between items-center space-x-4">
        <div className="flex items-center space-x-2">
          {/* Search field */}
          <div className="w-[235px]">
            <div className="relative">
              <InputDebounce
                placeholder="Search Event Name"
                onDebouncedChange={(value) => onSearchChange(value || '')}
                className="w-full border border-[#d9d9d9] rounded-sm px-3 text-sm h-[32px]"
                style={{ paddingRight: '40px', fontSize: '14px' }}
                size="small"
              />
              <div className="absolute right-0 top-0 h-full flex items-center justify-center px-3 border-l border-[#d9d9d9]">
                <SearchOutlined className="text-[rgba(0,0,0,0.25)]" />
              </div>
            </div>
          </div>

          {/* Status Tabs */}
          <StatusTabs activeTab={activeTab} tabs={statusTabs} onTabChange={onTabChange} />
        </div>

        {/* Filter buttons */}
        <div className="flex space-x-2 h-[32px]">
          <FilterPopover
            label="Owner"
            items={owners}
            selectedItems={selectedOwners}
            onSelectionChange={onOwnersChange}
            visible={ownerFilterVisible}
            onVisibleChange={onOwnerFilterVisibleChange}
            renderItem={(owner) => (
              <div className="flex items-center">
                <div className="w-6 h-6 rounded-full bg-[#EB1964] text-white flex items-center justify-center text-xs mr-2">
                  {owner.name.charAt(0).toUpperCase()}
                </div>
                <span>{owner.name}</span>
              </div>
            )}
          />

          <FilterPopover
            label="Category"
            items={categories}
            selectedItems={selectedCategories}
            onSelectionChange={onCategoriesChange}
            visible={categoryFilterVisible}
            onVisibleChange={onCategoryFilterVisibleChange}
          />

          <DateRangeFilter
            selectedRange={selectedDateRange}
            onChange={onDateRangeChange}
            visible={dateFilterVisible}
            onVisibleChange={onDateFilterVisibleChange}
          />

          <ClearFiltersButton disabled={!hasActiveFilters} onClear={onClearFilters} />
        </div>
      </div>
    </div>
  )
}
